import { Kpi as GqlKpi } from "@acto/gql/graphql";
import { Kpi } from "@acto/taskpane/core/domain";
import { toGqlKpi } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import {
  formatCurrency,
  formatKpiDelta,
  formatKpiValue,
  formatProfitWithMargin,
} from "@acto/taskpane/field-sales-2/utils/format";
import { useNumberFormatter } from "@acto/taskpane/core/utils/hooks/useNumberFormatter.tsx";
import type { ProductTrendItem } from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";

export interface ProductKpiValueData {
  kpi: GqlKpi;
  value: number | null;
  change: { percentageChange: number | null } | null;
  previousValue?: number | null;
}

export interface ProductGroupNode {
  id: string;
  productGroup: string;
  externalReference: string | null;
  hasChildren: boolean;
  values: ProductKpiValueData[];
}

// The KPI the list is ordered by, and that the trend filter gates on. Marge is
// profit-backed in the UI (the row shows profit, the badge shows profit's
// change), so it maps to PROFIT.
export function toOrderKpi(selectedKpi: Kpi): GqlKpi {
  switch (selectedKpi) {
    case Kpi.PROFIT:
    case Kpi.MARGIN:
      return GqlKpi.Profit;
    case Kpi.ORDERS:
      return GqlKpi.Orders;
    default:
      return GqlKpi.Revenue;
  }
}

const findValue = (group: ProductGroupNode, kpi: GqlKpi): ProductKpiValueData | undefined =>
  group.values.find((value) => value.kpi === kpi);

const baseItem = (
  group: ProductGroupNode,
  data: ProductKpiValueData | undefined,
  formatNumber: ReturnType<typeof useNumberFormatter>,
): Omit<ProductTrendItem, "formattedValue"> => {
  const change = data?.change?.percentageChange ?? null;
  return {
    id: group.id,
    name: group.productGroup,
    externalReference: group.externalReference,
    hasChildren: group.hasChildren,
    value: data?.value ?? 0,
    change,
    formattedChange: change != null ? formatNumber({ number: change, type: "percentage", addPlusSign: true }) : null,
  };
};

/** A row of a period listing: the KPI's value in the selected period. */
export function toProductValueItem(
  group: ProductGroupNode,
  selectedKpi: Kpi,
  locale: string,
  currency: CurrencyCode,
  formatNumber: ReturnType<typeof useNumberFormatter>,
): ProductTrendItem {
  // The MARGIN tab renders profit-backed: the row shows profit with the margin alongside, the
  // badge shows profit's change.
  if (selectedKpi === Kpi.MARGIN) {
    const profit = findValue(group, GqlKpi.Profit);
    const margin = findValue(group, GqlKpi.Margin)?.value ?? null;
    const formattedValue =
      profit?.value != null ? formatProfitWithMargin(profit.value, margin, currency, locale) : null;
    return { ...baseItem(group, profit, formatNumber), formattedValue };
  }

  const data = findValue(group, toGqlKpi(selectedKpi));
  const formattedValue = data?.value != null ? formatKpiValue(selectedKpi, data.value, currency, locale) : null;
  return { ...baseItem(group, data, formatNumber), formattedValue };
}

export function toOrderIntakeValueItem(
  group: ProductGroupNode,
  locale: string,
  currency: CurrencyCode,
  formatNumber: ReturnType<typeof useNumberFormatter>,
): ProductTrendItem {
  const data = findValue(group, GqlKpi.IncomingOrder);
  const formattedValue = data?.value != null ? formatCurrency(data.value, currency, locale) : null;
  return { ...baseItem(group, data, formatNumber), formattedValue };
}

/**
 * A row of a comparison listing: currency and count KPIs render the change between the compared
 * windows, so the rows sum to the parent the user drilled from. The server omits zero KPIs (they
 * arrive null), so absence on one side reads as zero rather than depending on that serialisation.
 * Margin is a ratio and does not sum: the MARGIN tab renders each row's margin in the current
 * window with the margin's own change badge, blank on rows without current activity.
 */
export function toProductDeltaItem(
  group: ProductGroupNode,
  selectedKpi: Kpi,
  locale: string,
  currency: CurrencyCode,
  formatNumber: ReturnType<typeof useNumberFormatter>,
): ProductTrendItem {
  if (selectedKpi === Kpi.MARGIN) {
    const margin = findValue(group, GqlKpi.Margin);
    const formattedValue = margin?.value != null ? formatKpiValue(Kpi.MARGIN, margin.value, currency, locale) : null;
    return { ...baseItem(group, margin, formatNumber), formattedValue };
  }

  const data = findValue(group, toGqlKpi(selectedKpi));
  const formattedValue =
    data != null && (data.value != null || data.previousValue != null)
      ? formatKpiDelta(selectedKpi, (data.value ?? 0) - (data.previousValue ?? 0), currency, locale)
      : null;
  return { ...baseItem(group, data, formatNumber), formattedValue };
}
